import { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';

/**
 * ONE shared source for GET /api/leads/package-requests.
 *
 * Before this, AISupportAgent (the count badge) and PackageRequestsDock (the
 * card) each fetched this endpoint on their own mount + 25s interval, so one
 * page load hit it 2-3 times. This singleton fetches ONCE on first mount,
 * refreshes on ONE 25s interval, dedupes concurrent/rapid callers, and exposes
 * refresh() so handling a request re-reads immediately (never stale).
 */
let _state = { requests: [], count: 0, loaded: false };
const _subs = new Set();
let _inflight = null;
let _interval = null;
let _debounce = null;
let _teardown = null;

function _emit() { for (const fn of _subs) fn(_state); }

function _fetch() {
  if (_inflight) return _inflight;
  _inflight = apiClient
    .get('/leads/package-requests')
    .then((res) => {
      const d = res.data || {};
      const requests = Array.isArray(d.requests) ? d.requests : [];
      _state = {
        requests,
        count: Number(d.count != null ? d.count : requests.length),
        loaded: true,
      };
      _emit();
    })
    .catch(() => { /* silent — a failed poll must never break the shell */ })
    .finally(() => { _inflight = null; });
  return _inflight;
}

function _scheduleRefresh() {
  clearTimeout(_debounce);
  _debounce = setTimeout(() => { _fetch(); }, 400);
}

export function refreshPackageRequests() { _scheduleRefresh(); }

export default function usePackageRequests() {
  const [state, setState] = useState(_state);
  useEffect(() => {
    _subs.add(setState);
    if (_subs.size === 1) {
      if (!_state.loaded && !_inflight) _fetch();
      _interval = setInterval(() => { _fetch(); }, 25000);
      _teardown = () => {
        clearInterval(_interval);
        _interval = null;
        clearTimeout(_debounce);
      };
    } else if (!_state.loaded && !_inflight) {
      _fetch();
    }
    return () => {
      _subs.delete(setState);
      if (_subs.size === 0 && _teardown) { _teardown(); _teardown = null; }
    };
  }, []);
  return { ...state, refresh: _scheduleRefresh };
}
